<template>
  <v-col v-orphans cols="12" class="pa-0 faq-list section-gap-bottom">
    <h2 class="section-heading sz-primary-grey--text f-24 font-weight-medium text-center">
      {{ $t('label.faq') }}
    </h2>
    <v-expansion-panels v-model="panel" variant="accordion" class="faq-panels" flat>
      <v-expansion-panel v-for="(item, index) in faq" :key="index" class="faq-panels__item">
        <v-expansion-panel-title
          :id="getQuestionId(index)"
          class="faq-panels__question"
          @click="goToQuestion(index)"
        >
          {{ item.header }}
          <template #actions="{ expanded }">
            <span class="faq-panels__icon" aria-hidden="true">{{ expanded ? '×' : '+' }}</span>
          </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="faq-panels__answer">
          <div v-html="item.content" />
          <div v-if="item?.button" class="my-3 mx-2 d-flex justify-center">
            <BaseButton :href="item.button?.href">
              {{ item.button?.text }}
            </BaseButton>
          </div>
          <div v-if="item?.link" class="mt-3">
            <a target="_blank" class="primary-link" :href="item.link.href">{{ item.link.text }}</a>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
    <slot>
      <v-row no-gutters justify="center" class="mt-10">
        <v-col cols="auto">
          <BaseButton :href="LINKS.guide" variant="secondary" arrow>
            {{ $t('label.showGuide') }}
          </BaseButton>
        </v-col>
      </v-row>
    </slot>
  </v-col>
</template>

<script setup lang="ts">
import BaseButton from '@/components/widgets/BaseButton.vue'
import { LINKS } from '@/constants/externalLinks'
import { ref } from 'vue'
import type { Maybe } from '@/types'
import { useGoTo } from 'vuetify'
import type { FaqItem } from '@/types/faq.ts'

type Props = {
  faq?: FaqItem[]
}

const goTo = useGoTo()

withDefaults(defineProps<Props>(), {
  faq: () => []
})

const panel = ref<Maybe<number>>(null)

const getQuestionId = (index: number): string => 'faq-question-' + index

const goToQuestion = function (index: number) {
  setTimeout(() => goTo('#' + getQuestionId(index)), 250)
}
</script>

<style lang="scss">
// Design System (docs/design-system), section 12 - FAQ / Accordions
.faq-panels {
  --faq-ink: #2a1715;
  --faq-ink-600: #6b4f49;
  --faq-stone-dark: #d9d5ce;
  --faq-stone-light: #f5f4f1;
  --faq-closed-bg: #efefef;
  --faq-focus: #2855b6;

  gap: 8px;

  &.v-expansion-panels > .faq-panels__item.v-expansion-panel {
    background-color: #fff;
    color: var(--faq-ink);
    border: 1px solid var(--faq-stone-dark);
    border-radius: 30px !important;
    margin: 0;
    overflow: hidden;

    &::after {
      display: none;
    }

    &::before {
      box-shadow: none;
    }

    transition:
      transform 0.18s ease,
      box-shadow 0.18s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 44px -24px rgb(42 23 21 / 28%);
    }
  }

  .faq-panels__question.v-expansion-panel-title {
    min-height: 0;
    padding: 18px 20px;
    font-family: Poppins, 'Segoe UI', system-ui, sans-serif;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
    color: var(--faq-ink);
    background-color: var(--faq-closed-bg);
    transition:
      background-color 0.15s,
      color 0.15s;

    .v-expansion-panel-title__overlay {
      display: none;
    }

    .v-expansion-panel-title__icon {
      margin-inline-start: 12px;
    }

    &.v-expansion-panel-title--active {
      min-height: 0;
      background-color: var(--faq-ink);
      color: #fff;
    }

    // a11y: visible keyboard focus (inside the card, because the card has overflow: hidden)
    &:focus-visible {
      outline: 3px solid var(--faq-focus);
      outline-offset: -3px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &.v-expansion-panels > .faq-panels__item.v-expansion-panel {
      transition: none;

      &:hover {
        transform: none;
      }
    }

    .faq-panels__question.v-expansion-panel-title {
      transition: none;
    }
  }

  .faq-panels__icon {
    display: block;
    width: 20px;
    font-size: 20px;
    font-weight: 400;
    line-height: 1;
    text-align: center;
  }

  .faq-panels__answer {
    border-top: 1px solid var(--faq-stone-light);

    // 16px at the request of the business (the Design System, section 12, has 14.5px)
    font-size: 16px;
    line-height: 1.7;
    color: var(--faq-ink-600);

    .v-expansion-panel-text__wrapper {
      padding: 14px 20px 20px;
    }
  }
}
</style>
